import { Getter } from '../Getter';
import { HttpError } from '../HttpError';
import { MetaAggregate, ApiLanguage } from './Models/MetaAggregate';
import {
  OrganizationBreakdown,
  FileType,
} from './Models/OrganizationBreakdown';
import isValidUUID from 'uuid-validate';
import URI from 'urijs';

type ApiResponse = MetaAggregate | null | undefined;

interface DataResponse {
  data?: OrganizationBreakdown;
  error?: never;
}
interface ErrorResponse {
  data?: never;
  error: HttpError;
}
export type { OrganizationBreakdown };
export type ClientOutput = DataResponse | ErrorResponse;

export class OrganizationBreakdownClient {
  private getter: Getter<ApiResponse | HttpError>;

  constructor(getter: Getter<ApiResponse | HttpError>) {
    this.getter = getter;
  }

  public async retrieveAsync(
    organizationUUID: string,
    isFetchingOnly30Days = false
  ): Promise<ClientOutput> {
    if (!isValidUUID(organizationUUID)) {
      // equivalent to the response we'd get on the server
      return {
        error: new HttpError(400),
      };
    }

    const response = await this.tryRetrieveAsync(
      organizationUUID,
      isFetchingOnly30Days
    );

    if (!response) {
      return {
        data: undefined,
        error: undefined,
      };
    }

    if (response instanceof HttpError) {
      return {
        error: response,
      };
    }

    return {
      data: {
        fileTypes: response.languages.map(toFileType),
        numTotalBytes: response.total_bytes,
        numTotalFiles: response.total_files,
        numTotalLines: response.total_lines,
      },
    };
  }

  private async tryRetrieveAsync(
    organizationUUID: string,
    isFetchingOnly30Days: boolean
  ): Promise<ApiResponse | HttpError> {
    const href = getHref(organizationUUID, isFetchingOnly30Days);

    return await this.getter.get(href);
  }
}

export function getHref(
  organizationUUID: string,
  isFetchingOnly30Days: boolean
): string {
  const uri = URI('/api/v1/organizations')
    .segment(organizationUUID)
    .segment('meta-aggregate');

  if (isFetchingOnly30Days) {
    uri.search({ for_period: '1' });
  }

  return uri.toString();
}

export function toFileType(language: ApiLanguage): FileType {
  return {
    name: language.name,
    numBytes: language.num_bytes,
    numFiles: language.num_files,
    numLines: language.num_lines,
  };
}
